import { useState } from 'react';
import { Modal, Form, Input, InputNumber, Button, Alert, Typography, Space, message } from 'antd';
import { CopyOutlined } from '@ant-design/icons';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { provisioningApi } from '../../api/provisioning';
import type { CreateEnrollmentResult } from '../../types';

interface Props {
  open: boolean;
  onClose: () => void;
  firmId: number;
  tenantId: string | null;
  erpType: string;
}

export function AgentKurModal({ open, onClose, firmId, tenantId, erpType }: Props) {
  const queryClient = useQueryClient();
  const [result, setResult] = useState<CreateEnrollmentResult | null>(null);
  const [expiresInDays, setExpiresInDays] = useState<number | null>(7);

  const createMutation = useMutation({
    mutationFn: () => provisioningApi.create(firmId, expiresInDays),
    onSuccess: (r) => {
      setResult(r);
      queryClient.invalidateQueries({ queryKey: ['enrollments', firmId] });
      queryClient.invalidateQueries({ queryKey: ['firm', firmId] });
    },
    onError: () => message.error('Enrollment oluşturulamadı'),
  });

  const handleClose = () => {
    setResult(null);            // token'ı bellekten temizle — bir daha gösterilmez
    createMutation.reset();     // mutation.data'daki token kopyası da gitsin
    setExpiresInDays(7);
    onClose();
  };

  const copy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      message.success('Kopyalandı');
    } catch {
      message.error('Kopyalanamadı — değeri elle seçip kopyalayın');
    }
  };

  return (
    <Modal
      title="Agent Kur"
      open={open}
      onCancel={handleClose}
      footer={result ? [<Button key="ok" type="primary" onClick={handleClose}>Kapat</Button>] : null}
      destroyOnHidden
    >
      {!result ? (
        <Form layout="vertical" onFinish={() => createMutation.mutate()}>
          <Form.Item label="ERP Tipi">
            <Input value={erpType} disabled />
          </Form.Item>
          <Form.Item label="İttihat Tenant">
            <Input value={tenantId ?? 'Otomatik (İttihat oluşturacak)'} disabled />
          </Form.Item>
          <Form.Item label="Son kullanma (gün, boş = süresiz)">
            <InputNumber min={1} value={expiresInDays ?? undefined}
              onChange={(v) => setExpiresInDays(v ?? null)} style={{ width: '100%' }} />
          </Form.Item>
          <Button type="primary" htmlType="submit" loading={createMutation.isPending} block>
            Enrollment Oluştur
          </Button>
        </Form>
      ) : (
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <Alert type="warning" showIcon
            message="Token yalnızca bir kez gösterilir"
            description="Bu pencere kapanınca token bir daha gösterilmez. Kopyalayıp müşteriye iletin." />
          <div>
            <Typography.Text strong>Enrollment Key</Typography.Text>
            <Space.Compact style={{ width: '100%' }}>
              <Input value={result.token} readOnly />
              <Button icon={<CopyOutlined />} onClick={() => copy(result.token)} />
            </Space.Compact>
          </div>
          <div>
            <Typography.Text strong>Installer</Typography.Text>
            <Space.Compact style={{ width: '100%' }}>
              <Input value={result.installerUrl} readOnly />
              <Button icon={<CopyOutlined />} onClick={() => copy(result.installerUrl)} />
            </Space.Compact>
          </div>
        </Space>
      )}
    </Modal>
  );
}
